import { Divider, Grid, IconButton, Link, Typography } from "@mui/material";
import axios from "axios";
import { format } from "date-fns";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import DebouncedAutoComplete from "../components/DebouncedAutoComplete";
import Iconify from "../../minimals/components/iconify";
import { OrderDocumentCategory } from "../documents/types";
import { Attachment } from "./types/Attachment";

const formatUploadDate = (value?: string) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return format(date, "MM/dd/yyyy");
};

const flattenOrderDocuments = (categories: OrderDocumentCategory[]): Attachment[] =>
  categories.flatMap((category) =>
    category.subcategories.flatMap((subcategory) =>
      subcategory.documents.map((document) => ({
        id: document.id,
        date1: document.date1,
        category: document.category || category.name,
        subcategory: document.subcategory || subcategory.name,
        description: document.documentDescription || ""
      }))
    )
  );

export default function OrderAttachments({
  selectedAttachments,
  setSelectedAttachments,
  orderNumber,
  showUploadDate = false
}: {
  selectedAttachments: Attachment[];
  setSelectedAttachments: Dispatch<SetStateAction<Attachment[]>>;
  orderNumber: string | undefined;
  showUploadDate?: boolean;
}) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [documentsWithDates, setDocumentsWithDates] = useState<Attachment[] | null>(null);
  //this is a hack to clear the attachments autocomplete allowing it to be uncontrolled :)
  // const [autocompleteKey, setAutocompleteKey] = useState<string>(random(1, 10, true).toString());
  const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;

  const handleTextChange = async (text: string, signal?: AbortSignal) => {
    if (showUploadDate) {
      if (!text.trim()) {
        setAttachments([]);
        return;
      }

      let searchableDocuments = documentsWithDates;
      if (!searchableDocuments) {
        const { data } = await axios.get<OrderDocumentCategory[]>(
          `/api/orderdocuments/getall/${encodeURIComponent(orderNumber || "")}`,
          { signal }
        );
        searchableDocuments = flattenOrderDocuments(data);
        setDocumentsWithDates(searchableDocuments);
      }

      const normalizedText = text.toLowerCase();
      setAttachments(
        searchableDocuments.filter((attachment) => attachment.description.toLowerCase().includes(normalizedText))
      );
      return;
    }

    const { data } = await axios.get(`/api/orderdocuments/search?ordernumber=${orderNumber}&searchtext=${text}`, {
      signal
    });
    setAttachments(data);
  };

  const addAttachment = (attachment: Attachment | null | undefined) => {
    if (!attachment) {
      return;
    }

    setSelectedAttachments((currentAttachments) =>
      currentAttachments.some((selectedAttachment) => selectedAttachment.id === attachment.id)
        ? currentAttachments
        : [...currentAttachments, attachment]
    );
  };

  const handleOptionChange = (
    e: React.SyntheticEvent<Element, Event>,
    option: string | Attachment | null | undefined
  ) => {
    if (option && typeof option === "object") {
      addAttachment(option);
    }
  };

  const handleDeleteAttachment = (id: number) => {
    setSelectedAttachments(selectedAttachments.filter((a) => a.id !== id));
  };

  useEffect(() => {
    setDocumentsWithDates(null);
    setAttachments([]);
  }, [orderNumber, showUploadDate]);

  return (
    <>
      <DebouncedAutoComplete
        sx={{ marginBottom: 3 }}
        //this is a hack to clear the attachments autocomplete allowing it to be uncontrolled :)
        // key={autocompleteKey}
        options={attachments}
        getOptionsLabel={(v) => v?.description || ""}
        onSelectedOptionChanged={handleOptionChange}
        textboxLabel="Order Documents"
        textboxPlaceholder="Type to search"
        onDebouncedTextChanged={handleTextChange}
        disableCloseOnSelect
        preserveInputOnSelect
        renderOption={(props, option) => {
          const uploadedDate = showUploadDate ? formatUploadDate(option?.date1) : "";

          return (
            <li {...props} key={option?.id}>
              <div style={{ width: "100%" }}>
                <div>
                  <Grid container spacing={1}>
                    <Grid item xs={10}>
                      <Typography variant="subtitle2" color="primary">
                        {option?.description}
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {option?.category}/{option?.subcategory}
                      </Typography>
                      {uploadedDate && (
                        <Typography variant="caption" color="text.secondary">
                          Uploaded {uploadedDate}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={2}>
                      <IconButton
                        color="primary"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => {
                          event.stopPropagation();
                          addAttachment(option);
                        }}
                      >
                        <Iconify icon="eva:plus-square-fill" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </div>
              </div>
            </li>
          );
        }}
      />
      {selectedAttachments.length > 0 && <Divider sx={{ marginTop: 3, marginBottom: 3 }} />}
      {selectedAttachments.map((a) => (
        <Grid container key={a.id}>
          <Grid item xs={1}>
            <Iconify icon="eva:attach-outline" marginTop={0.5} />
          </Grid>
          <Grid item xs={10}>
            <Link
              sx={{ marginBottom: 1, display: "block" }}
              href={`${momentumUrl}opendoc?id=${a.id}`}
              target="_blank"
            >
              {a.description}
            </Link>
            {showUploadDate && formatUploadDate(a.date1) && (
              <Typography variant="caption" color="text.secondary" display="block" marginBottom={1}>
                Uploaded {formatUploadDate(a.date1)}
              </Typography>
            )}
          </Grid>
          <Grid item xs={1}>
            <IconButton size="small" color="error" onClick={() => handleDeleteAttachment(a.id)}>
              <Iconify icon="eva:trash-2-outline" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
    </>
  );
}
